import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import { VpHoverInfo } from '../components';
import { Stack } from '@mui/material';

// for the case where option?.value is false
const getOptionValue = (opt) => {
  return opt?.id !== undefined ? opt?.id : opt;
};

export const VpHookFormSelect = ({
  name = '',
  defaultValue = '',
  required = false,
  rules = {},
  disabled = false,
  onChange = null,
  isLoading = false,
  placeholder = '',
  label = '',
  helperText = '',
  options = [],
  disableHelperText = false,
  smallLabel = false,
  renderOptions = null,
  selectDefault = false,
  infoProps = {},
  ...rest
}) => {
  const id = `vp-hook-select-${name}`;
  const vpInputProps = {
    ...(isLoading
      ? {
          startAdornment: (
            <InputAdornment position="start">
              <CircularProgress size={24} />
            </InputAdornment>
          )
        }
      : {})
  };

  // prevents issues where defaultValue is null
  const fallbackDefaultValue = selectDefault ? getOptionValue(options?.[0]) || '' : '';
  const defaultInputValue =
    defaultValue || defaultValue === false ? defaultValue : fallbackDefaultValue;
  const { setValue } = useFormContext();
  const {
    field: { onChange: onControllerChange, onBlur, value, name: inputName, ref },
    fieldState: { error }
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue: defaultInputValue,
    shouldUnregister: true
  });

  useEffect(() => {
    setValue(name, defaultInputValue);
  }, [defaultInputValue]);

  // helper text spacing will always be there so we don't "push down" elements
  const errorMessage = error?.message;
  const defaultHelperText = disableHelperText ? '' : <span>&nbsp;</span>;
  const helperTextMessage = errorMessage || helperText || defaultHelperText;

  const onSelectChange = (e) => {
    onControllerChange(e);
    onChange && onChange(e);
  };

  return (
    <FormControl fullWidth variant="outlined">
      {label && (
        <Stack direction="row" gap="5px">
          <Typography htmlFor={id} variant={smallLabel ? 'inputLabelSmall' : 'inputLabel'}>
            {label}
          </Typography>
          {infoProps && <VpHoverInfo {...infoProps} />}
        </Stack>
      )}
      <Select
        id={id}
        onChange={onSelectChange}
        onBlur={onBlur}
        value={value}
        inputRef={ref}
        name={inputName}
        disabled={disabled}
        size="small"
        error={!!error}
        placeholder={placeholder}
        {...vpInputProps}
        {...rest}
      >
        {renderOptions
          ? renderOptions
          : options.map((opt) => (
              <MenuItem key={getOptionValue(opt)} value={getOptionValue(opt)}>
                {opt.label || opt}
              </MenuItem>
            ))}
      </Select>
      {helperTextMessage && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};

VpHookFormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string])),
  disableHelperText: PropTypes.bool,
  smallLabel: PropTypes.bool,
  renderOptions: PropTypes.node,
  selectDefault: PropTypes.bool,
  infoProps: PropTypes.object
};

export default VpHookFormSelect;



// WEBPACK FOOTER //
// ./src/vp-ui/inputs/VpHookFormSelect.jsx